import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Question from './Question';
import Button from './Button';
import { Container, Typography } from '@material-ui/core';
import { useCoolfoodContext } from './Store';
import calculateGains from '../calculationTools';
import { actions } from '../state/reducer';
import NumberOfBeneficiaries from './NumberOfBeneficiaries';
import { i18n } from '../App';

import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  buttons: {
    ...theme.buttons,
    marginTop: theme.spacing(8)
    // marginBottom: theme.spacing(10)
  },
  name: {
    fontSize: 20,
    marginTop: 16,
    fontWeight: 600
  },
  stepTitle: theme.stepTitle
}));

const Commitment = React.memo(function Commitment({ definition }) {
  const [globalState, globalDispatch] = useCoolfoodContext();

  const [fullAnswer, setFullAnswer] = useState({
    id: definition.id,
    now: null,
    commitment: null,
    numberOfBeneficiaries: 0,
    gains: null
  });

  const [disabled, setDisabled] = useState(true);

  const classes = useStyles();

  let helpNow = '<span class="letter">A.</span>';
  helpNow += definition.helpNow
    ? definition.helpNow
    : i18n._('Essayez d\'indiquer vos habitudes alimentaires actuelles');

  let helpCommitment =
    definition.qNow && definition.nowType !== 'none'
      ? '<span class="letter">B.</span>'
      : '<span class="letter">A.</span>';
  helpCommitment += definition.helpCommitment
    ? definition.helpCommitment
    : i18n._('Essayez d\'indiquer la nature de votre engagement');

  let helpBeneficiaries =
    definition.qNow && definition.nowType !== 'none'
      ? '<span class="letter">C.</span>'
      : '<span class="letter">B.</span>';
  helpBeneficiaries += definition.helpBeneficiaries
    ? definition.helpBeneficiaries
    : i18n._(
      'Indiquez le nombre de personnes de votre foyer qui relèvent ce défi'
    );

  // Register answer into state
  useEffect(() => {
    if (fullAnswer.commitment) {
      let isValid;
      // Compare now
      isValid =
        definition.nowType !== 'none' && fullAnswer.now && fullAnswer.now.value
          ? true
          : false;
      // Number of beneficiaries
      isValid =
        !!definition.askForNumberOfBeneficiaries &&
        fullAnswer.numberOfBeneficiaries
          ? true
          : false;
      // Set disabled status of next button
      setDisabled(!isValid);
      // Store commit in global store
      if (isValid) {
        globalDispatch({ type: actions.SET_COMMIT, commit: fullAnswer });
      }
    }
  }, [definition.askForNumberOfBeneficiaries, definition.nowType, fullAnswer, fullAnswer.commitment, globalDispatch]);

  useEffect(() => {
    if (definition.nowType !== 'none' && !fullAnswer.now) {
      globalDispatch({
        type: actions.SHOW_HELP,
        content: helpNow
      });
    }
  }, [globalDispatch, fullAnswer.now, definition.nowType, helpNow]);

  useEffect(() => {
    if (
      ((definition.nowType !== 'none' && fullAnswer.now) ||
        definition.nowType === 'none') &&
      !fullAnswer.commitment
    ) {
      globalDispatch({
        type: actions.SHOW_HELP,
        content: helpCommitment
      });
    }
    // globalState.selectedCommitments.length == 0
  }, [globalDispatch, fullAnswer.now, definition.nowType, definition.qNow, fullAnswer.commitment, helpCommitment]);

  useEffect(() => {
    if (
      ((definition.nowType !== 'none' && fullAnswer.now) ||
        definition.nowType === 'none') &&
      fullAnswer.commitment &&
      (fullAnswer.numberOfBeneficiaries === 0 || helpBeneficiaries)
    ) {
      globalDispatch({
        type: actions.SHOW_HELP,
        content: helpBeneficiaries
      });
    }
    // globalState.selectedCommitments.length == 0
  }, [globalDispatch, fullAnswer.now, definition.nowType, definition.qNow, fullAnswer.commitment, fullAnswer.numberOfBeneficiaries, helpBeneficiaries]);

  const updateAnswer = useCallback(
    (answerType, answer) => {
      setFullAnswer(oldAnswer => {
        const fa = { ...oldAnswer, [answerType]: answer };
        let gains;
        // Gain calculation
        // Do not calculate gain if we don't already have the commitment response
        if (fa.commitment) {
          const commitmentDefinition = globalState.happening.commitments.find(
            c => c.id === definition.id
          );
          gains = calculateGains(commitmentDefinition, fa);
        }
        return { ...fa, gains };
      });
    },
    [definition.id, globalState.happening.commitments]
  );

  // Get previous answer stored in the globalState (from cookie for example)
  const getPreviousAnswer = answerType => {
    if (!globalState.commits) {
      return;
    }
    let commit = globalState.commits.filter(c => c.id === definition.id);
    if (commit && commit[0] && commit[0][answerType]) {
      return commit[0][answerType];
    }
  };

  // Get default value if defined in WP side
  const getDefaultValue = defaultValue => {
    let commit = globalState.happening.commitments.filter(c => c.id === definition.id);
    if (commit && commit[0] && commit[0]['default_value_validated']) {
      return commit[0]['default_value_validated'];
    }
  };

  return (
    <Container id={`commitment_${definition.id}`}>
      <Typography className={`ribbon ${classes.stepTitle}`}>
        {i18n._('ETAPE 3 : Renseignez les informations de vos défis')}
      </Typography>

      <Typography className={`commitmentName ${classes.name}`}>
        {definition.name}
      </Typography>

      {!!definition.qNow && definition.nowType !== 'none' && (
        <Question
          prefix="A."
          label={definition.qNow}
          type={definition.nowType}
          icon={definition.nowIcon}
          suffix={definition.qNowSuffix}
          suffixPlural={definition.qNowSuffixPlural}
          choices={definition.qNowChoice}
          numbers={definition.qNowNumber}
          display={definition.qNowDisplay}
          commitmentID={definition.id}
          answerType="now"
          onChange={updateAnswer}
          previousAnswer={getPreviousAnswer('now')}
        />
      )}
      <Question
        prefix={definition.qNow && definition.nowType !== 'none' ? 'B.' : 'A.'}
        label={definition.qCommitment}
        type={definition.answerType}
        icon={definition.icon}
        suffix={definition.qSuffix}
        suffixPlural={definition.qSuffixPlural}
        numbers={definition.qNumber}
        choices={definition.qChoice}
        display={definition.qDisplay}
        commitmentID={definition.id}
        answerType="commitment"
        onChange={updateAnswer}
        previousAnswer={getPreviousAnswer('commitment')}
        defaultValue={getDefaultValue()}
      />
      {!!definition.askForNumberOfBeneficiaries && (
        <NumberOfBeneficiaries
          prefix={
            definition.qNow && definition.nowType !== 'none' ? 'C.' : 'B.'
          }
          commitmentID={definition.id}
          onChange={updateAnswer}
          previousAnswer={getPreviousAnswer('numberOfBeneficiaries')}
        />
      )}
      <div className={classes.buttons}>
        <Button
          color="primary"
          onClick={() => globalDispatch({ type: actions.PREV_SLIDE })}
          className="prevButton"
        >
          {i18n._('Précédent')}
        </Button>
        <Button
          color="primary"
          disabled={disabled}
          variant="contained"
          className="nextButton"
          onClick={() => globalDispatch({ type: actions.NEXT_SLIDE })}
        >
          {i18n._('Suivant')}
        </Button>
      </div>
    </Container>
  );
});

Commitment.propTypes = {
  definition: PropTypes.shape().isRequired
};

export default Commitment;
